import React, { memo } from 'react';
import { LABEL_TYPES } from './labelConstants';
import { IA_CATEGORIES } from './iaCategories';

const CAT_SVG = {
  // ── Catégories IA ────────────────────────────────────────────────────────
  Animal: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <ellipse cx="7" cy="9.5" rx="4" ry="3.5" fill={color} />
      <circle cx="3" cy="5.5" r="1.8" fill={color} />
      <circle cx="7" cy="4" r="1.8" fill={color} />
      <circle cx="11" cy="5.5" r="1.8" fill={color} />
    </svg>
  ),
  Construction: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <path d="M7,1.5 C3.8,1.5 1.5,4.5 1.5,8 L12.5,8 C12.5,4.5 10.2,1.5 7,1.5Z" fill={color} />
      <rect x="1" y="8.5" width="12" height="2" rx="1" fill={color} />
    </svg>
  ),
  Domestique: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <polygon points="7,0.5 13.5,7 11,7 11,13.5 3,13.5 3,7 0.5,7" fill={color} />
    </svg>
  ),
  Musique: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <ellipse cx="5" cy="12" rx="3" ry="2.2" fill={color} />
      <rect x="7.5" y="3.5" width="1.5" height="8.5" rx="0.5" fill={color} />
      <path d="M9,3.5 Q13,5.5 13,9 Q11,7 9,8 Z" fill={color} />
    </svg>
  ),
  Urbain: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <rect x="0.5" y="6.5" width="3.5" height="7" fill={color} />
      <rect x="5" y="3" width="4" height="10.5" fill={color} />
      <rect x="9.5" y="5" width="4" height="8.5" fill={color} />
    </svg>
  ),
  'Voix humaine': (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <rect x="4.5" y="0.5" width="5" height="8" rx="2.5" fill={color} />
      <path
        d="M2,7.5 C2,11.5 5,13 7,13 C9,13 12,11.5 12,7.5"
        stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"
      />
      <line x1="7" y1="13" x2="7" y2="13.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Véhicule: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <rect x="1" y="7.5" width="12" height="4" rx="1" fill={color} />
      <path d="M3.5,7.5 L5,4.5 L9,4.5 L10.5,7.5 Z" fill={color} />
      <circle cx="4" cy="11.5" r="1.8" fill="white" />
      <circle cx="10" cy="11.5" r="1.8" fill="white" />
    </svg>
  ),
  // ── Label manuel (cercle orange) ─────────────────────────────────────────
  diamond: (color, size) => (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 3} cy={size / 3} r={size / 2 - 1} fill={color} />
    </svg>
  ),
  // ── IA sans catégorie détectée (onde sonore) ──────────────────────────────
  circle: (color, size) => (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <rect x="1"   y="6"   width="1.5" height="2"  rx="0.7" fill={color} />
      <rect x="3.5" y="4"   width="1.5" height="6"  rx="0.7" fill={color} />
      <rect x="6"   y="2"   width="1.5" height="10" rx="0.7" fill={color} />
      <rect x="8.5" y="4"   width="1.5" height="6"  rx="0.7" fill={color} />
      <rect x="11"  y="6"   width="1.5" height="2"  rx="0.7" fill={color} />
    </svg>
  ),
};

export const GeoIcon = memo(({ iconType, color: colorProp, size = 14 }) => {
  const catEntry = IA_CATEGORIES[iconType];
  const labelEntry = !catEntry
    ? Object.values(LABEL_TYPES).find((t) => t.iconType === iconType)
    : null;
  const color = colorProp ?? catEntry?.color ?? labelEntry?.color ?? '#666';
  const render = CAT_SVG[iconType] ?? CAT_SVG.circle;
  return render(color, size);
});

export const PencilIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
  </svg>
);

export const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
    <path
      fillRule="evenodd"
      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
    />
  </svg>
);
